import React, { useEffect } from "react";
import { getAuth, onAuthStateChanged } from "@firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { UserInfo, actions, User } from "../../store";
import { connect } from "react-redux";
import LoginForm from "./login_form";
import backgroundImage from "./cover.png";
import bgImage2 from "./cover.jpg";
import logo from "../../assets/logo.png";
import paths from "../../routes/paths";
import { Card, Typography, Space, Divider } from "antd";
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

interface P {
  loggedIn: (info: User) => void;
}

function Login(props: P) {
  let Navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const authListener = onAuthStateChanged(getAuth(), async (authUser) => {
      if (authUser) {
        let path = paths.home;
        let searchParams: string | null = null;
        const token = await authUser.getIdTokenResult();
        if (token.claims) {
          props.loggedIn(token.claims as User);
        }
        if (location.state?.from) {
          path = location.state.from;
        }
        if (location.state?.search) {
          searchParams = location.state.search;
        }
        if (searchParams) {
          Navigate(
            { pathname: path, search: `?${searchParams}` },
            { replace: true }
          );
        } else {
          Navigate(path, { replace: true });
        }
      }
    });
    return authListener;
  }, [props, location]);

  return (
    <div style={styles.container}>
      {/* Background with overlay */}
      <div style={styles.backgroundOverlay}></div>
      
      {/* Left side - Welcome content */}
      <div style={styles.leftSection}>
        <div style={styles.welcomeContent}>
          <div style={styles.logoContainer}>
            <img src={logo} alt="Logo" style={styles.logo} />
          </div>
          <Title level={1} style={styles.welcomeTitle}>
            Welcome to Great Admin
          </Title>
          <Text style={styles.welcomeSubtitle}>
            Your comprehensive business management solution
          </Text>
          <div style={styles.features}>
            <Space direction="vertical" size="large" style={styles.featureList}>
              <div style={styles.featureItem}>
                <UserOutlined style={styles.featureIcon} />
                <div>
                  <Text strong style={styles.featureTitle}>User Management</Text>
                  <br />
                  <Text type="secondary">Manage users and permissions efficiently</Text>
                </div>
              </div>
              <div style={styles.featureItem}>
                <LockOutlined style={styles.featureIcon} />
                <div>
                  <Text strong style={styles.featureTitle}>Secure Access</Text>
                  <br />
                  <Text type="secondary">Enterprise-grade security with Google OAuth</Text>
                </div>
              </div>
              <div style={styles.featureItem}>
                <SafetyCertificateOutlined style={styles.featureIcon} />
                <div>
                  <Text strong style={styles.featureTitle}>Data Protection</Text>
                  <br />
                  <Text type="secondary">Your data is protected with industry standards</Text>
                </div>
              </div>
            </Space>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div style={styles.rightSection}>
        <Card style={styles.loginCard} bordered={false}>
          <div style={styles.loginHeader}>
            <Title level={2} style={styles.loginTitle}>
              Sign In
            </Title>
            <Text type="secondary" style={styles.loginSubtitle}>
              Access your dashboard with your Google account
            </Text>
          </div>
          
          <Divider style={styles.divider} />
          
          <div style={styles.loginForm}>
            <LoginForm />
          </div>
          
          <div style={styles.footer}>
            <Text type="secondary" style={styles.footerText}>
              By signing in, you agree to our Terms of Service and Privacy Policy
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
}

const mapStateToDispatch = (dispatch: any) => {
  return {
    loggedIn: (info: User) => dispatch(actions.authAction.loggedIn(info)),
  };
};

export default connect(null, mapStateToDispatch)(Login) as any;

const styles = {
  container: {
    display: "flex",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%), url(${backgroundImage})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative" as const,
    flexDirection: "row" as const,
    "@media (max-width: 768px)": {
      flexDirection: "column" as const,
    },
  },
  backgroundOverlay: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    zIndex: 1,
  },
  leftSection: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    position: "relative" as const,
    zIndex: 2,
    "@media (max-width: 768px)": {
      padding: "20px",
      minHeight: "40vh",
    },
  },
  rightSection: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    position: "relative" as const,
    zIndex: 2,
    "@media (max-width: 768px)": {
      padding: "20px",
      minHeight: "60vh",
    },
  },
  welcomeContent: {
    maxWidth: "500px",
    textAlign: "center" as const,
    "@media (max-width: 768px)": {
      maxWidth: "100%",
    },
  },
  logoContainer: {
    marginBottom: "30px",
    "@media (max-width: 768px)": {
      marginBottom: "20px",
    },
  },
  logo: {
    width: "80px",
    height: "auto",
    filter: "brightness(0) invert(1)",
    "@media (max-width: 768px)": {
      width: "60px",
    },
  },
  welcomeTitle: {
    color: "#ffffff",
    fontSize: "48px",
    fontWeight: 700,
    marginBottom: "16px",
    textShadow: "0 2px 4px rgba(0,0,0,0.3)",
    "@media (max-width: 768px)": {
      fontSize: "32px",
      marginBottom: "12px",
    },
  },
  welcomeSubtitle: {
    color: "rgba(255, 255, 255, 0.9)",
    fontSize: "18px",
    marginBottom: "60px",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
    "@media (max-width: 768px)": {
      fontSize: "16px",
      marginBottom: "40px",
    },
  },
  features: {
    textAlign: "left" as const,
    "@media (max-width: 768px)": {
      display: "none",
    },
  },
  featureList: {
    width: "100%",
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "16px",
  },
  featureIcon: {
    fontSize: "24px",
    color: "#ffffff",
    marginTop: "4px",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
  },
  featureTitle: {
    color: "#ffffff",
    fontSize: "16px",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
  },
  loginCard: {
    width: "100%",
    maxWidth: "450px",
    borderRadius: "16px",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
    backgroundColor: "#ffffff",
    padding: "40px",
    "@media (max-width: 768px)": {
      maxWidth: "100%",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)",
    },
  },
  loginHeader: {
    textAlign: "center" as const,
    marginBottom: "32px",
    "@media (max-width: 768px)": {
      marginBottom: "24px",
    },
  },
  loginTitle: {
    marginBottom: "8px",
    color: "#1a1a1a",
    fontWeight: 600,
    "@media (max-width: 768px)": {
      fontSize: "24px",
    },
  },
  loginSubtitle: {
    fontSize: "16px",
    color: "#666666",
    "@media (max-width: 768px)": {
      fontSize: "14px",
    },
  },
  divider: {
    margin: "24px 0",
    borderColor: "#f0f0f0",
    "@media (max-width: 768px)": {
      margin: "16px 0",
    },
  },
  loginForm: {
    marginBottom: "32px",
    "@media (max-width: 768px)": {
      marginBottom: "24px",
    },
  },
  footer: {
    textAlign: "center" as const,
  },
  footerText: {
    fontSize: "12px",
    lineHeight: "1.5",
    "@media (max-width: 768px)": {
      fontSize: "11px",
    },
  },
};
